/**
 * Modulo M4: presenze. Regola di calcolo dei minuti, pura e senza dipendenze.
 *
 * - Ogni ingresso/uscita è un intervallo con un canale (online o sala). Un intervallo aperto
 *   (fine null) vale fino all'istante di calcolo, o alla fine effettiva della diretta.
 * - Si conta l'UNIONE degli intervalli di tutti i canali e dispositivi: due schede aperte o
 *   "in sala" più "online" nello stesso momento contano una volta sola.
 * - Si conta solo dentro [inizioEffettivo, fineEffettiva]: prima dell'avvio e dopo la chiusura
 *   non maturano minuti.
 * - I minuti totali sono arrotondati per difetto sul totale, non sui singoli intervalli.
 * - Percentuale = minuti totali / durata ufficiale (non arrotondata: la soglia si confronta
 *   con il valore esatto, l'interfaccia mostra l'intero).
 */
import { z } from 'zod';
import { MODALITA_PARTECIPAZIONE, type ModalitaPartecipazione } from './percorsi.js';

export const CANALI_PRESENZA = MODALITA_PARTECIPAZIONE; // 'online' | 'sala'
export type CanalePresenza = ModalitaPartecipazione;
export const ORIGINI_PRESENZA = ['heartbeat', 'dichiarazione', 'manuale', 'chiusura_automatica'] as const;
export type OriginePresenza = (typeof ORIGINI_PRESENZA)[number];
export const STATI_PLAYER = ['in_riproduzione', 'in_buffer', 'fermo', 'errore'] as const;
export type StatoPlayer = (typeof STATI_PLAYER)[number];

export const REGOLE_PRESENZA = {
  /** versione della regola salvata in presence_summary, da alzare se il calcolo cambia */
  versione: 1,
  heartbeatOgniSecondi: 30,
  /** senza heartbeat per questo tempo l'intervallo online si chiude */
  chiusuraDopoSecondi: 90,
  /** "Sono in sala" accettato da questo anticipo sull'inizio previsto */
  salaApertaDaMinuti: 60,
} as const;

export interface IntervalloPresenza {
  inizio: Date;
  fine: Date | null;
  canale: CanalePresenza;
}

export interface ParametriCalcolo {
  intervalli: IntervalloPresenza[];
  inizioEffettivo: Date | null;
  fineEffettiva: Date | null;
  durataUfficialeMin: number;
  /** istante di calcolo per gli intervalli aperti (default: adesso) */
  adesso?: Date;
}

export interface Segmento {
  inizio: Date;
  fine: Date;
}

export interface EsitoPresenza {
  minutiOnline: number;
  minutiSala: number;
  /** unione di tutti i canali: è il valore che conta per la soglia */
  minutiTotali: number;
  /** 0..100 e oltre (se la diretta dura più del previsto), non arrotondata */
  percentuale: number;
  /** segmenti dell'unione, già ritagliati sulla finestra della diretta */
  segmenti: Segmento[];
}

function unisci(segmenti: Segmento[]): Segmento[] {
  const ordinati = [...segmenti].filter((s) => s.fine > s.inizio).sort((a, b) => a.inizio.getTime() - b.inizio.getTime());
  const out: Segmento[] = [];
  for (const s of ordinati) {
    const ultimo = out[out.length - 1];
    if (ultimo && s.inizio <= ultimo.fine) {
      if (s.fine > ultimo.fine) ultimo.fine = s.fine;
    } else {
      out.push({ inizio: s.inizio, fine: s.fine });
    }
  }
  return out;
}

function durataMs(segmenti: Segmento[]): number {
  return segmenti.reduce((acc, s) => acc + (s.fine.getTime() - s.inizio.getTime()), 0);
}

function ritaglia(intervalli: IntervalloPresenza[], da: Date, a: Date, canale?: CanalePresenza): Segmento[] {
  const out: Segmento[] = [];
  for (const i of intervalli) {
    if (canale && i.canale !== canale) continue;
    const inizio = i.inizio > da ? i.inizio : da;
    const fineReale = i.fine ?? a;
    const fine = fineReale < a ? fineReale : a;
    if (fine > inizio) out.push({ inizio, fine });
  }
  return out;
}

export function calcolaPresenza(p: ParametriCalcolo): EsitoPresenza {
  const vuoto: EsitoPresenza = { minutiOnline: 0, minutiSala: 0, minutiTotali: 0, percentuale: 0, segmenti: [] };
  if (!p.inizioEffettivo || p.durataUfficialeMin <= 0) return vuoto;
  const adesso = p.adesso ?? new Date();
  const fineFinestra = p.fineEffettiva ?? adesso;
  if (fineFinestra <= p.inizioEffettivo) return vuoto;

  const tutti = unisci(ritaglia(p.intervalli, p.inizioEffettivo, fineFinestra));
  const online = unisci(ritaglia(p.intervalli, p.inizioEffettivo, fineFinestra, 'online'));
  const sala = unisci(ritaglia(p.intervalli, p.inizioEffettivo, fineFinestra, 'sala'));
  const minutiTotali = Math.floor(durataMs(tutti) / 60_000);
  return {
    minutiOnline: Math.floor(durataMs(online) / 60_000),
    minutiSala: Math.floor(durataMs(sala) / 60_000),
    minutiTotali,
    percentuale: (minutiTotali / p.durataUfficialeMin) * 100,
    segmenti: tutti,
  };
}

/** Soglia del test: il 75,0% esatto basta (decisione del committente). */
export function sopraSoglia(percentuale: number, sogliaPct: number): boolean {
  return percentuale >= sogliaPct;
}

/** "1 h 42 min" / "42 min", per il contatore e i report. */
export function formattaMinuti(minuti: number): string {
  const h = Math.floor(minuti / 60);
  const m = minuti % 60;
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

// ---------------------------------------------------------------- Schemi API
const id = z.string().uuid();
const dataIso = z.string().datetime({ offset: true });

export const heartbeatSchema = z.object({
  webinarId: id,
  /** identificativo della scheda/dispositivo generato dal client (sessionStorage), per distinguere più schede */
  clientId: z.string().min(8).max(64),
  statoPlayer: z.enum(STATI_PLAYER),
  trasporto: z.enum(['webrtc', 'hls', 'nessuno']).default('nessuno'),
  schedaAttiva: z.boolean().default(true),
});
export type HeartbeatInput = z.infer<typeof heartbeatSchema>;

export const presenzaMiaSchema = z.object({
  minutiTotali: z.number().int(),
  minutiOnline: z.number().int(),
  minutiSala: z.number().int(),
  percentuale: z.number(),
  sogliaPct: z.number().int(),
  sopraSoglia: z.boolean(),
  durataUfficialeMin: z.number().int(),
  /** intervallo online aperto per questa scheda */
  intervalloAperto: z.boolean(),
  /** dichiarazione "Sono in sala" aperta */
  inSala: z.boolean(),
  inSalaDa: dataIso.nullable(),
  /** la diretta è avviata: i minuti contano */
  conteggioAttivo: z.boolean(),
  oraServer: dataIso,
});
export type PresenzaMia = z.infer<typeof presenzaMiaSchema>;

export const intervalloSchema = z.object({
  id,
  canale: z.enum(CANALI_PRESENZA),
  inizio: dataIso,
  fine: dataIso.nullable(),
  origine: z.enum(ORIGINI_PRESENZA),
  nota: z.string().nullable(),
  operatore: z.string().nullable(),
  annullato: z.boolean(),
});
export type Intervallo = z.infer<typeof intervalloSchema>;

/** Riga di "In sala adesso" (admin). */
export const inSalaSchema = z.object({
  userId: id,
  nome: z.string(),
  cognome: z.string(),
  inSalaDa: dataIso,
  /** sta mandando heartbeat online nello stesso momento */
  ancheOnline: z.boolean(),
  dispositivo: z.string().nullable(),
});
export type InSala = z.infer<typeof inSalaSchema>;

/** Riepilogo presenze per iscritto (admin). */
export const presenzaIscrittoSchema = z.object({
  userId: id,
  nome: z.string(),
  cognome: z.string(),
  email: z.string(),
  modalitaConferma: z.enum(CANALI_PRESENZA).nullable(),
  minutiOnline: z.number().int(),
  minutiSala: z.number().int(),
  minutiTotali: z.number().int(),
  percentuale: z.number(),
  sopraSoglia: z.boolean(),
  collegatoOra: z.boolean(),
  inSalaOra: z.boolean(),
  /** dichiarato in sala e collegato online insieme */
  incoerenza: z.boolean(),
  ultimoHeartbeatAt: dataIso.nullable(),
});
export type PresenzaIscritto = z.infer<typeof presenzaIscrittoSchema>;

export const correzioneManualeSchema = z
  .object({
    userId: id,
    canale: z.enum(CANALI_PRESENZA),
    inizio: dataIso,
    fine: dataIso,
    nota: z.string().trim().min(3, 'Scrivi il motivo della correzione.').max(500),
  })
  .refine((c) => new Date(c.fine) > new Date(c.inizio), { message: "L'uscita deve essere dopo l'ingresso.", path: ['fine'] });
export type CorrezioneManuale = z.infer<typeof correzioneManualeSchema>;

export const annullaIntervalloSchema = z.object({ nota: z.string().trim().min(3, 'Scrivi il motivo.').max(500) });
export const chiudiIntervalloSchema = z.object({ fine: dataIso.optional(), nota: z.string().trim().min(3, 'Scrivi il motivo.').max(500) });

export const riepilogoPresenzeSchema = z.object({
  collegatiOra: z.number().int(),
  inSalaOra: z.number().int(),
  sopraSoglia: z.number().int(),
  totaleConfermati: z.number().int(),
  incoerenze: z.number().int(),
});
